import constate from 'constate';
import { nets, tf } from 'face-api.js';
import { useEffect, useRef, useState } from 'react';

import { getLogger } from '@/idv/utils';

const { logInfo, logWarn, logError } = getLogger({ location: 'id-doc' });

type FaceModelLoaderType = {
  isSelfieRequired: boolean;
};

export type FaceModelState = {
  modelsLoaded: boolean;
  /**
   * Autocapture is off for the rest of the session — the models could not load, or inference
   * failed. The manual shutter still works.
   */
  modelsUnavailable: boolean;
  /** Logs the first inference failure of the session, then latches modelsUnavailable. */
  reportInferenceFailure: (err: unknown) => void;
};

const useFaceModelLoader = ({ isSelfieRequired }: FaceModelLoaderType): FaceModelState => {
  // bifrost, hosted and handoff ship only these two nets in public/model; another net needs its files copied there.
  const MODEL_URL = '/model';
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [modelsUnavailable, setModelsUnavailable] = useState(false);
  const inferenceFailedRef = useRef(false);

  const reportInferenceFailure = (err: unknown) => {
    // The ref, not the state, is the latch: setState only lands a render later, while detection
    // ticks keep arriving every SELFIE_CHECK_INTERVAL.
    if (inferenceFailedRef.current) {
      return;
    }
    inferenceFailedRef.current = true;
    // A GPU that can't run the model: autocapture turns off and manual capture carries the flow.
    logWarn('Face detection failed at inference; disabling autocapture', err, { backend: tf.getBackend() });
    setModelsUnavailable(true);
  };

  // Loading models take time; therefore we want to load them asynchronously before we need them
  // This reduces the detection time when you are on selfie page by removing the time to load the models
  useEffect(() => {
    const loadModels = async () => {
      if (!isSelfieRequired) {
        return;
      }

      try {
        await tf.ready();
        const backend = tf.getBackend();
        if (backend !== 'webgl') {
          // face-api on cpu is ~1000ms/image and the detection tick doesn't serialize, so cpu
          // piles up main-thread work instead of ever converging on a capture.
          logWarn('No WebGL backend for face detection; autocapture disabled', undefined, { backend });
          setModelsUnavailable(true);
          return;
        }

        await Promise.all([
          nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        ]);
        logInfo('Face models loaded', { backend });
        setModelsLoaded(true);
      } catch (err) {
        logError('Error loading the face models', err);
        setModelsUnavailable(true);
      }
    };
    loadModels();
  }, [isSelfieRequired]);

  return { modelsLoaded, modelsUnavailable, reportInferenceFailure };
};

const [FaceModelProvider, useFaceModel] = constate(useFaceModelLoader);

export { FaceModelProvider, useFaceModel };
